import type { Unit } from "../types/entities.js";

/**
 * Einheiten zusammenrechnen, ohne zu lügen.
 *
 * Der Plan dahinter, weil er mehr Entscheidung als Code ist:
 *
 * Nur drei Gruppen lassen sich wirklich umrechnen: Gewicht (g, kg), Volumen
 * (ml, l) und Anzahl (Stück). Alles andere, was die App als Einheit kennt,
 * ist ein Gebinde: Packung, Dose, Glas, Flasche, Bund, Becher. Wie viel in
 * einer Packung Nudeln steckt, weiß niemand, und eine Zahl zu erfinden wäre
 * schlimmer als keine zu haben: Der Vorratsabgleich zöge dann Mengen ab, die
 * es nie gab.
 *
 * Deshalb ist jedes Gebinde eine eigene Gruppe. "2 Dosen Tomaten" und
 * "400 g Tomaten" bleiben nebeneinander stehen, statt sich zu einer falschen
 * Zahl zu addieren. Was nebeneinander steht, sieht man auf der Liste, und
 * eine Zahl, die man sieht, kann man korrigieren.
 *
 * Eine Zutat ohne Einheit ("Salz", "etwas Öl") ist eine eigene Gruppe: Sie
 * addiert sich mit anderen einheitenlosen Angaben, aber nie mit Gramm.
 */

/** Rechengruppe einer Einheit. Nur innerhalb einer Gruppe wird umgerechnet. */
export type Dimension = "masse" | "volumen" | "anzahl" | "ohne" | Unit;

interface Regel {
  dimension: Dimension;
  /** Wie viele Grundeinheiten (g, ml, Stück) eine Einheit wert ist. */
  faktor: number;
}

const REGELN: Record<Unit, Regel> = {
  g: { dimension: "masse", faktor: 1 },
  kg: { dimension: "masse", faktor: 1000 },
  ml: { dimension: "volumen", faktor: 1 },
  l: { dimension: "volumen", faktor: 1000 },
  stk: { dimension: "anzahl", faktor: 1 },
  // Gebinde: jedes für sich, Faktor 1 in der eigenen Gruppe.
  pkg: { dimension: "pkg", faktor: 1 },
  dose: { dimension: "dose", faktor: 1 },
  glas: { dimension: "glas", faktor: 1 },
  flasche: { dimension: "flasche", faktor: 1 },
  bund: { dimension: "bund", faktor: 1 },
  becher: { dimension: "becher", faktor: 1 },
};

/** Grundeinheit je Gruppe. In ihr wird gerechnet, nicht angezeigt. */
const GRUNDEINHEIT: Partial<Record<Dimension, Unit>> = {
  masse: "g",
  volumen: "ml",
  anzahl: "stk",
};

export function dimensionOf(unit: Unit | null): Dimension {
  return unit === null ? "ohne" : REGELN[unit].dimension;
}

/** Lassen sich diese beiden Einheiten überhaupt verrechnen? */
export function sameDimension(a: Unit | null, b: Unit | null): boolean {
  return dimensionOf(a) === dimensionOf(b);
}

export interface Menge {
  quantity: number | null;
  unit: Unit | null;
}

/**
 * Menge in der Grundeinheit ihrer Gruppe. `null` heißt: keine Zahl genannt,
 * zum Beispiel "Salz". Das ist etwas anderes als die Zahl 0.
 */
export function toBase(menge: Menge): number | null {
  if (menge.quantity === null) return null;
  if (menge.unit === null) return menge.quantity;
  return menge.quantity * REGELN[menge.unit].faktor;
}

/**
 * Aus einer Menge in der Grundeinheit wieder eine lesbare Menge.
 *
 * 1500 g werden zu 1,5 kg, 900 g bleiben 900 g. Die Schwelle liegt bei der
 * vollen großen Einheit: "0,9 kg Mehl" liest sich im Laden schlechter als
 * "900 g Mehl".
 */
export function fromBase(wert: number, dimension: Dimension): Menge {
  if (dimension === "ohne") return { quantity: runde(wert), unit: null };

  if (dimension === "masse") {
    return wert >= 1000 ? { quantity: runde(wert / 1000), unit: "kg" } : { quantity: runde(wert), unit: "g" };
  }
  if (dimension === "volumen") {
    return wert >= 1000 ? { quantity: runde(wert / 1000), unit: "l" } : { quantity: runde(wert), unit: "ml" };
  }

  const einheit = GRUNDEINHEIT[dimension] ?? (dimension as Unit);
  return { quantity: runde(wert), unit: einheit };
}

/** Drei Nachkommastellen, wie die Spalte in der Datenbank. Ohne -0. */
function runde(wert: number): number {
  return Math.round(wert * 1000) / 1000 + 0;
}

/**
 * Rechnet eine Menge in eine andere Einheit um, oder `null`, wenn die beiden
 * nicht zusammenpassen. Kein Näherungswert, keine Annahme.
 */
export function convert(menge: Menge, ziel: Unit | null): number | null {
  if (!sameDimension(menge.unit, ziel)) return null;
  const basis = toBase(menge);
  if (basis === null) return null;
  return ziel === null ? runde(basis) : runde(basis / REGELN[ziel].faktor);
}

/**
 * Addiert Mengen, gruppiert nach dem, was sich verrechnen lässt.
 *
 * Kommt in einer Gruppe eine Angabe ohne Zahl vor ("Salz"), bleibt die Menge
 * der Gruppe `null`: Die App weiß dann, dass etwas gebraucht wird, aber nicht
 * wie viel, und schreibt lieber gar keine Zahl als eine erfundene.
 */
export function addQuantities(mengen: readonly Menge[]): Menge[] {
  const gruppen = new Map<Dimension, { summe: number; ohneZahl: boolean }>();

  for (const menge of mengen) {
    const dimension = dimensionOf(menge.unit);
    const stand = gruppen.get(dimension) ?? { summe: 0, ohneZahl: false };
    const basis = toBase(menge);
    if (basis === null) stand.ohneZahl = true;
    else stand.summe += basis;
    gruppen.set(dimension, stand);
  }

  return [...gruppen].map(([dimension, stand]) =>
    stand.summe === 0 && stand.ohneZahl ? { quantity: null, unit: anzeigeEinheit(dimension) } : fromBase(stand.summe, dimension),
  );
}

function anzeigeEinheit(dimension: Dimension): Unit | null {
  if (dimension === "ohne") return null;
  return GRUNDEINHEIT[dimension] ?? (dimension as Unit);
}

/**
 * Was nach Abzug des Vorrats noch fehlt.
 *
 * Passen die Einheiten nicht zusammen, wird nichts abgezogen: Lieber einmal
 * zu viel kaufen als das Abendessen ohne Zutat. Reicht der Vorrat, kommt
 * `null` zurück, dann muss nichts auf die Liste.
 */
export function subtractStock(gebraucht: Menge, vorrat: Menge): Menge | null {
  if (!sameDimension(gebraucht.unit, vorrat.unit)) return gebraucht;

  const brauche = toBase(gebraucht);
  const habe = toBase(vorrat) ?? 0;
  // Ohne Zahl ("Salz") lässt sich nichts abziehen. Wer Salz im Vorrat hat,
  // braucht es trotzdem nicht auf die Liste, deshalb gilt der Vorrat als
  // ausreichend, sobald überhaupt etwas da ist.
  if (brauche === null) return habe > 0 ? null : gebraucht;

  const rest = brauche - habe;
  if (rest <= 0) return null;
  return fromBase(rest, dimensionOf(gebraucht.unit));
}
